'use client';

// ── Max Contract Size · "Example · 2 Mini / 20 Micro" ─────────────────────────
// Figma: Moldura · Exemplo · contratos — desktop 12071:59926 (792×350),
// mobile 12471:8165 (366×612). Bar segments are percentages of the 480px
// desktop bar so the 318px mobile bar keeps the same proportions.

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ExampleLabel, FigureFrame } from '../primitives';
import { CheckmarkIcon, CloseIcon } from '../Icons';

const BAR = 480;
const pct = (px: number) => `${(px / BAR) * 100}%`;

type Segment = { left: number; width: number; label: string; tone: 'mini' | 'micro' | 'over' };

const SEGMENT_TONE: Record<Segment['tone'], string> = {
  mini: 'bg-[#228e71] text-white',
  micro: 'bg-[#e2f3ef] text-[#196551]',
  over: 'bg-[#ac0003]/[0.16] text-[#ac0003]',
};

const MINI_A: Segment = { left: 0, width: 190, label: 'MINI', tone: 'mini' };
const MINI_B: Segment = { left: 192, width: 190, label: 'MINI', tone: 'mini' };
const MICRO_10: Segment = { left: 192, width: 190, label: '10 MICRO', tone: 'micro' };
const MICRO_20: Segment = { left: 0, width: 382, label: '20 MICRO', tone: 'micro' };
const OVER_5: Segment = { left: 384, width: 94, label: '+5', tone: 'over' };

const ROWS: { label: string; segments: Segment[]; status: string; ok: boolean }[] = [
  { label: '2 Mini', segments: [MINI_A, MINI_B], status: '20/20', ok: true },
  { label: '20 Micro', segments: [MICRO_20], status: '20/20', ok: true },
  { label: '1 Mini + 10 Micro', segments: [MINI_A, MICRO_10], status: '20/20', ok: true },
  { label: '1 Mini + 15 Micro', segments: [MINI_A, MICRO_10, OVER_5], status: '25/20', ok: false },
];

function Chip({ tone, children }: { tone: 'mini' | 'micro'; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-[0.25rem] px-[1.25rem] py-[0.25rem] font-geist text-[2rem] font-medium leading-[3rem]',
        SEGMENT_TONE[tone],
      )}
    >
      {children}
    </span>
  );
}

export default function ContractSizes() {
  return (
    <FigureFrame data-ui="figure.contractSizes" className="gap-[2.5rem]">
      <ExampleLabel className="lg:whitespace-nowrap">
        EXAMPLE · MAX CONTRACT SIZE <br className="lg:hidden" />2 MINI / 20 MICRO
      </ExampleLabel>

      {/* Legend */}
      <div data-ui="figure.contractSizes.legend" className="flex w-full flex-wrap items-center gap-x-[3rem] gap-y-[1rem]">
        <Chip tone="mini">1 Mini</Chip>
        <span className="font-geist text-[2rem] leading-[3rem] text-black/40">=</span>
        <Chip tone="micro">10 Micro</Chip>
        <p className="mb-0 font-geist text-[2rem] leading-[3rem] text-black/64">Limit: 20 Micro in total — the black line</p>
      </div>

      {/* Rows */}
      <div data-ui="figure.contractSizes.rows" className="flex w-full flex-col gap-[2.5rem] lg:gap-[1.25rem]">
        {ROWS.map((row) => (
          <div
            key={row.label}
            data-ui="figure.contractSizes.row"
            className="flex w-full flex-col gap-[1rem] lg:flex-row lg:items-center lg:gap-[2rem]"
          >
            <div className="flex w-full items-center justify-between lg:contents">
              <p className="mb-0 font-geist text-[2rem] font-medium leading-[3rem] text-black lg:order-1 lg:min-w-0 lg:flex-1">
                {row.label}
              </p>
              <div
                className={cn(
                  'flex shrink-0 items-center gap-[0.75rem] font-geist text-[2rem] font-medium leading-[3rem] lg:order-3',
                  row.ok ? 'text-[#228e71]' : 'text-[#ac0003]',
                )}
              >
                {row.ok ? <CheckmarkIcon className="h-[2rem] w-[2rem]" /> : <CloseIcon className="h-[2rem] w-[2rem]" />}
                <span className="whitespace-nowrap">{row.status}</span>
              </div>
            </div>
            <div
              data-ui="figure.contractSizes.bar"
              className="relative h-[3.5rem] w-full shrink-0 rounded-[0.25rem] bg-black/[0.04] lg:order-2 lg:w-[60rem]"
            >
              {row.segments.map((seg) => (
                <div
                  key={seg.label + seg.left}
                  className={cn(
                    'absolute top-0 flex h-[3.5rem] items-end justify-center overflow-hidden rounded-[0.25rem] font-geist text-[2rem] font-medium leading-[3rem]',
                    SEGMENT_TONE[seg.tone],
                  )}
                  style={{ left: pct(seg.left), width: pct(seg.width) }}
                >
                  <span className="whitespace-nowrap">{seg.label}</span>
                </div>
              ))}
              <div
                data-ui="figure.contractSizes.limit"
                className="absolute top-[-0.5rem] h-[4.5rem] w-[0.25rem] bg-black"
                style={{ left: pct(383) }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div data-ui="figure.contractSizes.note" className="font-geist text-[2rem] leading-[3rem] text-black/64 lg:w-[81.75rem]">
        <p className="mb-0">
          <span className="font-medium text-[#228e71]">Allowed,</span> for example: 1 Mini + 10 Micro at the same time.
        </p>
        <p className="mb-0">
          <span className="font-medium text-[#ac0003]">Not possible:</span> 1 Mini + 15 Micro — that is 5 Micro more than
          the limit.
        </p>
      </div>
    </FigureFrame>
  );
}
